import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { UsersThree, Plus, Trash, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BulkMigrateDialog } from "../../components/BulkMigrateDialog";

function UserIssuanceOverrideSelect({ user, onChanged }) {
  const [busy, setBusy] = useState(false);
  const current = user.issuance_provider_override || "";

  const update = async (value) => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${user.id}/issuance-provider`, {
        provider: value || null,
      });
      const label = value ? `Cards now route via '${value}'` : "Override cleared — uses platform default";
      toast.success(`${user.email}: ${label}`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Override failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <select
      disabled={busy}
      value={current}
      onChange={(e) => update(e.target.value)}
      className={`text-[11px] mono rounded-md border px-2 py-1 bg-white disabled:opacity-50 ${
        current
          ? "border-[#CBA35C] text-[#8C6B21]"
          : "border-[#E5E0D8] text-[#4A5C50]"
      }`}
      data-testid={`user-issuance-select-${user.email}`}
      title={current ? `Per-user override active: ${current}` : "Uses the platform-wide ISSUANCE_PROVIDER setting"}
    >
      <option value="">platform default</option>
      <option value="mock">mock (wallet)</option>
      <option value="stripe">stripe (Visa)</option>
      <option value="nium">nium</option>
      <option value="wise">wise</option>
    </select>
  );
}

export function UsersTab({ users, customers, reload }) {
  const [open, setOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [migrateFor, setMigrateFor] = useState(null);
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="serif text-3xl">Users ({users.length})</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white" data-testid="create-user-btn"><Plus size={16} className="mr-1" /> Create user</Button>
          </DialogTrigger>
          <CreateUserDialog customers={customers} onDone={() => { setOpen(false); reload(); }} />
        </Dialog>
      </div>
      <OrphanedCardsPanel customers={customers} reload={reload} />
      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="text-left text-[#4A5C50] text-xs uppercase tracking-widest">
            <tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Assigned</th><th className="p-4">Issuance</th><th className="p-4">Created</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const assigned = Array.isArray(u.assigned_customers) ? u.assigned_customers : [];
              return (
              <tr key={u.id} className="border-t border-[#E5E0D8]">
                <td className="p-4">{u.full_name}</td>
                <td className="p-4 mono text-xs">{u.email}</td>
                <td className="p-4"><span className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#184A30]/10 text-[#184A30]">{u.role}</span></td>
                <td className="p-4 text-xs text-[#4A5C50]">
                  {u.role === "manager" ? (
                    assigned.length === 0
                      ? <span className="italic opacity-60">— none —</span>
                      : <span data-testid={`user-assigned-count-${u.email}`}>{assigned.length} customer{assigned.length === 1 ? "" : "s"}</span>
                  ) : <span className="opacity-40">—</span>}
                </td>
                <td className="p-4">
                  {u.role === "customer" ? (
                    <UserIssuanceOverrideSelect user={u} onChanged={reload} />
                  ) : (
                    <span className="opacity-40 text-xs">—</span>
                  )}
                </td>
                <td className="p-4 text-[#4A5C50] text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    {u.role === "manager" && (
                      <Button size="sm" variant="outline" className="rounded-lg border-[#184A30] text-[#184A30] hover:bg-[#184A30]/10"
                        onClick={() => setAssignFor(u)} data-testid={`assign-${u.email}`} title="Assign customers to this manager">
                        <UsersThree size={14} className="mr-1" /> Assign
                      </Button>
                    )}
                    {u.role === "customer" && (
                      <Button size="sm" variant="outline" className="rounded-lg border-[#CBA35C] text-[#8C6B21] hover:bg-[#CBA35C]/10"
                        onClick={() => setMigrateFor(u)} data-testid={`migrate-cards-${u.email}`}
                        title="Bulk-migrate this customer's wallet cards to Stripe Visa">
                        Migrate cards
                      </Button>
                    )}
                    {u.role !== "admin" && (
                      <Button size="sm" variant="outline" className="rounded-lg border-[#CBA35C] text-[#8C6B21] hover:bg-[#CBA35C]/10" onClick={async () => {
                        try {
                          const r = await api.post(`/admin/users/${u.id}/impersonate`);
                          const url = `${window.location.origin}/dashboard#imp=${encodeURIComponent(r.data.token)}`;
                          window.open(url, "_blank", "noopener");
                          toast.success(`Opened view-as ${u.email} in a new tab`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed to start view-as");
                        }
                      }} data-testid={`view-as-${u.email}`} title="View dashboard as this user">
                        <Eye size={14} className="mr-1" /> View as
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-[#D96C4A] hover:bg-[#D96C4A]/10" data-testid={`delete-user-${u.email}`} title={`Delete ${u.email}`}>
                          <Trash size={14} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#FBF9F6] border-[#E5E0D8]">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="serif text-2xl">Delete this user?</AlertDialogTitle>
                          <AlertDialogDescription className="text-[#4A5C50]">
                            Permanently remove <span className="mono text-[#112318]">{u.email}</span>. Their issued cards and
                            history stay intact but the account will no longer be able to sign in.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-xl" data-testid={`delete-user-cancel-${u.email}`}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="rounded-xl bg-[#D96C4A] text-white hover:bg-[#B04E2F]"
                            data-testid={`delete-user-confirm-${u.email}`}
                            onClick={async () => {
                              try { await api.delete(`/admin/users/${u.id}`); toast.success(`Deleted ${u.email}`); reload(); }
                              catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete user"); }
                            }}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      <Dialog open={!!assignFor} onOpenChange={(v) => { if (!v) setAssignFor(null); }}>
        {assignFor && (
          <AssignCustomersDialog
            manager={assignFor}
            customers={customers}
            onDone={() => { setAssignFor(null); reload(); }}
          />
        )}
      </Dialog>
      <BulkMigrateDialog
        open={!!migrateFor}
        user={migrateFor}
        onClose={() => { setMigrateFor(null); reload && reload(); }}
      />
    </div>
  );
}

function OrphanedCardsPanel({ customers, reload }) {
  const [data, setData] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/cards/orphaned"); setData(r.data); }
    catch { setData({ count: 0, cards: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data || data.count === 0) return null;

  const doReassign = async () => {
    if (!reassignTo) { toast.error("Pick a target user first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/cards/orphaned/reassign", { new_owner_id: reassignTo });
      toast.success(`Reassigned ${r.data.reassigned} cards to ${r.data.new_owner_email}`);
      await load(); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reassign"); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/admin/cards/orphaned");
      toast.success(`Deleted ${r.data.deleted} orphaned cards`);
      await load(); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-6 bg-[#CBA35C]/10 border border-[#CBA35C]/40 rounded-2xl p-5" data-testid="orphaned-cards-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#8C6B21]">Data cleanup</p>
          <h3 className="serif text-2xl mt-1">{data.count} orphaned card{data.count === 1 ? "" : "s"}</h3>
          <p className="text-sm text-[#4A5C50] mt-2 max-w-lg">
            These cards point to user accounts that no longer exist (likely from a previous seed reset). Reassign them to an active user to reclaim, or delete them permanently.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2 items-center">
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger className="w-64 rounded-xl bg-white border-[#E5E0D8]" data-testid="orphan-reassign-select">
                <SelectValue placeholder="Reassign to user…" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`orphan-target-${c.email}`}>
                    {c.full_name} · {c.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={doReassign} disabled={busy || !reassignTo}
              className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white" data-testid="orphan-reassign-btn">
              Reassign
            </Button>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="text-[#D96C4A] hover:bg-[#D96C4A]/10 text-xs" data-testid="orphan-delete-btn">
                <Trash size={14} className="mr-1" /> Delete all {data.count}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#FBF9F6] border-[#E5E0D8]">
              <AlertDialogHeader>
                <AlertDialogTitle className="serif text-2xl">Delete {data.count} orphaned cards?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#4A5C50]">
                  This permanently removes cards whose owner no longer exists. It cannot be undone. If you want to keep the history, reassign to a user instead.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-xl" data-testid="orphan-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={doDelete}
                  className="rounded-xl bg-[#D96C4A] text-white hover:bg-[#B04E2F]" data-testid="orphan-delete-confirm">
                  Delete permanently
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <details className="mt-3" data-testid="orphan-details">
        <summary className="text-xs cursor-pointer text-[#8C6B21] hover:text-[#184A30]">Show {data.count} affected cards</summary>
        <div className="mt-3 max-h-48 overflow-y-auto text-xs divide-y divide-[#CBA35C]/20">
          {data.cards.map((c) => (
            <div key={c.id} className="py-2 flex justify-between mono" data-testid={`orphan-row-${c.id}`}>
              <span className="truncate">{c.receiver_name} · {c.receiver_email}</span>
              <span className="shrink-0 opacity-70">•••• {c.last4 || "—"} · {c.status} · ${(Number(c.balance_vnd) || 0).toFixed(2)}</span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function AssignCustomersDialog({ manager, customers, onDone }) {
  const [selected, setSelected] = useState(() => new Set(manager.assigned_customers || []));
  const [q, setQ] = useState("");
  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const filtered = customers.filter((c) => {
    if (!q.trim()) return true;
    const n = q.trim().toLowerCase();
    return (c.email || "").toLowerCase().includes(n) || (c.full_name || "").toLowerCase().includes(n);
  });
  const submit = async () => {
    try {
      await api.patch(`/admin/users/${manager.id}/assign`, { assigned_customers: Array.from(selected) });
      toast.success(`Updated assignments for ${manager.email}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update assignments");
    }
  };
  return (
    <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-lg" data-testid="assign-customers-dialog">
      <DialogHeader>
        <DialogTitle className="serif text-3xl">Assign customers</DialogTitle>
        <DialogDescription className="text-[#4A5C50]">
          Pick which customer accounts <span className="mono text-[#112318]">{manager.email}</span> can view and manage.
        </DialogDescription>
      </DialogHeader>
      <div className="py-2 space-y-3">
        <input
          className="underline-input"
          placeholder="Search customers by email or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="assign-search"
        />
        <div className="flex items-center justify-between text-xs text-[#4A5C50]">
          <span>{selected.size} of {customers.length} selected</span>
          <div className="flex gap-3">
            <button type="button" className="link-ul" onClick={() => setSelected(new Set(customers.map((c) => c.id)))} data-testid="assign-all">Select all</button>
            <button type="button" className="link-ul" onClick={() => setSelected(new Set())} data-testid="assign-none">Clear</button>
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto border border-[#E5E0D8] rounded-xl bg-white divide-y divide-[#E5E0D8]" data-testid="assign-list">
          {filtered.length === 0 && <p className="p-4 text-sm text-[#4A5C50] italic">No customers match.</p>}
          {filtered.map((c) => {
            const checked = selected.has(c.id);
            return (
              <label key={c.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#FBF9F6]" data-testid={`assign-row-${c.email}`}>
                <input type="checkbox" className="w-4 h-4 accent-[#184A30]" checked={checked} onChange={() => toggle(c.id)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{c.full_name}</p>
                  <p className="mono text-xs text-[#4A5C50] truncate">{c.email}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" className="rounded-xl" onClick={onDone} data-testid="assign-cancel">Cancel</Button>
        <Button onClick={submit} className="rounded-xl bg-[#184A30] text-white" data-testid="assign-submit">Save assignments</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function CreateUserDialog({ customers, onDone }) {
  const [f, setF] = useState({ full_name: "", email: "", password: "", role: "manager", assigned_customers: [] });
  const toggleAssign = (id) => {
    setF((prev) => {
      const has = prev.assigned_customers.includes(id);
      return { ...prev, assigned_customers: has ? prev.assigned_customers.filter((x) => x !== id) : [...prev.assigned_customers, id] };
    });
  };
  const submit = async () => {
    if (!f.email || !f.password || !f.full_name) { toast.error("Name, email, and password are required"); return; }
    if (f.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    const payload = { ...f, assigned_customers: f.role === "manager" ? f.assigned_customers : [] };
    try { await api.post("/admin/users", payload); toast.success(`${f.role[0].toUpperCase()}${f.role.slice(1)} created`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-lg">
      <DialogHeader><DialogTitle className="serif text-3xl">Create user</DialogTitle>
      <DialogDescription className="text-[#4A5C50]">Admins can create managers (who oversee specific customers), additional admins, or customer accounts.</DialogDescription></DialogHeader>
      <div className="space-y-4 py-2">
        <div><Label className="text-xs uppercase tracking-widest text-[#4A5C50]">Full name</Label>
          <input className="underline-input" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="new-user-name" /></div>
        <div><Label className="text-xs uppercase tracking-widest text-[#4A5C50]">Email</Label>
          <input type="email" className="underline-input mono" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" /></div>
        <div><Label className="text-xs uppercase tracking-widest text-[#4A5C50]">Password</Label>
          <input type="password" className="underline-input mono" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password" />
          <p className="text-[11px] text-[#4A5C50] mt-1">Minimum 6 characters. Share this with the new user — they can change it after sign-in.</p>
        </div>
        <div><Label className="text-xs uppercase tracking-widest text-[#4A5C50]">Role</Label>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger className="mt-2 rounded-xl" data-testid="new-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin · full platform access</SelectItem>
              <SelectItem value="manager">Manager · oversees assigned customers</SelectItem>
              <SelectItem value="customer">Customer · can issue their own cards</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {f.role === "manager" && (
          <div data-testid="new-user-assign-section">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest text-[#4A5C50]">Assign customers ({f.assigned_customers.length})</Label>
              <div className="flex gap-3 text-xs">
                <button type="button" className="link-ul" onClick={() => setF({ ...f, assigned_customers: customers.map((c) => c.id) })} data-testid="new-user-assign-all">All</button>
                <button type="button" className="link-ul" onClick={() => setF({ ...f, assigned_customers: [] })} data-testid="new-user-assign-none">None</button>
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto border border-[#E5E0D8] rounded-xl bg-white divide-y divide-[#E5E0D8]">
              {customers.length === 0 && <p className="p-3 text-sm text-[#4A5C50] italic">No customer accounts yet — you can assign later.</p>}
              {customers.map((c) => {
                const checked = f.assigned_customers.includes(c.id);
                return (
                  <label key={c.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#FBF9F6]" data-testid={`new-user-assign-${c.email}`}>
                    <input type="checkbox" className="w-4 h-4 accent-[#184A30]" checked={checked} onChange={() => toggleAssign(c.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{c.full_name}</p>
                      <p className="mono text-xs text-[#4A5C50] truncate">{c.email}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-[#4A5C50] mt-2">Managers can only view cards and activity for their assigned customers.</p>
          </div>
        )}
      </div>
      <DialogFooter><Button onClick={submit} className="rounded-xl bg-[#184A30] text-white" data-testid="new-user-submit">Create</Button></DialogFooter>
    </DialogContent>
  );
}
